import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/authAPI';
import { backgroundCheckAPI } from '../services/backgroundCheckAPI';
import { formatAPIError } from '../utils/validation';
import Header from '../components/layout/Header';
import { useNavigate, Link } from 'react-router-dom';
import RequestBackgroundCheckModal from '../components/employer/RequestBackgroundCheckModal';
import './styles/Dashboard.css';

const EmployerDashboard = () => {
  const { user, logout, updateUser } = useAuth();

  const navigate = useNavigate();
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showMFASettings, setShowMFASettings] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false); // Request modal

  // Profile data state
  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Background checks state
  const [backgroundChecks, setBackgroundChecks] = useState([]);
  const [loadingChecks, setLoadingChecks] = useState(true);
  const [checksError, setChecksError] = useState('');

  // MFA Settings State
  const [mfaSettings, setMfaSettings] = useState({
    mfaEnabled: user?.mfaEnabled || false,
    mfaType: user?.mfaType || 'email'
  });
  const [tempSettings, setTempSettings] = useState({ ...mfaSettings });

  // ==================== LOAD PROFILE DATA ====================
const loadProfileData = useCallback(async () => {
  try {
    setLoadingProfile(true);
    console.log('🏢 EmployerDashboard: Loading profile data...');

    const response = await authAPI.getProfile();
    setProfileData(response.data);

    if (response.data) {
      updateUser(prev => ({
        ...prev,
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        email: response.data.account?.email,
        phone: response.data.account?.phone,
      }));
    }
  } catch (error) {
    console.error('❌ Failed to load profile:', error);
    setProfileData(null);
  } finally {
    setLoadingProfile(false);
  }
}, [updateUser]); // ✅ REMOVE user


useEffect(() => {
  loadProfileData();
}, [loadProfileData]);



  // ==================== LOAD BACKGROUND CHECKS ====================
  const loadBackgroundChecks = async () => {
    try {
      setLoadingChecks(true);
      setChecksError('');
      console.log('🔍 Loading background checks...');

      const result = await backgroundCheckAPI.getEmployerRequests();

      const checksArray = Array.isArray(result)
        ? result
        : Array.isArray(result?.data)
          ? result.data
          : [];

      setBackgroundChecks(checksArray);
      console.log('✅ Background checks loaded:', checksArray.length);
    } catch (error) {
      console.error('❌ Failed to load background checks:', error);
      setChecksError('Failed to load background checks');
      setBackgroundChecks([]);
    } finally {
      setLoadingChecks(false);
    }
  };


  useEffect(() => {
    loadBackgroundChecks();
  }, []);

  // Update MFA settings when user changes
  useEffect(() => {
    if (user) {
      setMfaSettings({
        mfaEnabled: user.mfaEnabled || false,
        mfaType: user.mfaType || 'email'
      });
      setTempSettings({
        mfaEnabled: user.mfaEnabled || false,
        mfaType: user.mfaType || 'email'
      });
    }
  }, [user]);

  // ==================== MFA HANDLERS ====================
  const handleMFAToggle = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const newMfaEnabled = !mfaSettings.mfaEnabled;
      await authAPI.updateMFA({
        mfaEnabled: newMfaEnabled,
        mfaType: tempSettings.mfaType
      });

      setMfaSettings({
        mfaEnabled: newMfaEnabled,
        mfaType: tempSettings.mfaType
      });

      setSuccess(
        newMfaEnabled
          ? `🎉 Two-Factor Authentication enabled via ${tempSettings.mfaType}!`
          : '✅ Two-Factor Authentication disabled successfully!'
      );

      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('❌ MFA update failed:', error);
      setError(formatAPIError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleMfaTypeChange = (newType) => {
    setTempSettings(prev => ({ ...prev, mfaType: newType }));
  };

  // ==================== BACKGROUND CHECK HANDLERS ====================
  const handleRequestSuccess = () => {
    setSuccess('🎉 Background check request submitted successfully!');
    loadBackgroundChecks(); // Reload the list
    setTimeout(() => setSuccess(''), 5000);
  };

  // ==================== HELPER FUNCTIONS ====================
  const getDisplayName = () => {
    if (loadingProfile) return 'Loading...';
    const firstName = profileData?.firstName || user?.firstName;
    const lastName = profileData?.lastName || user?.lastName;
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    return user?.email || 'Employer';
  };

  const getDisplayEmail = () => {
    return profileData?.account?.email || user?.email || 'Not provided';
  };

  const getDisplayPhone = () => {
    return profileData?.account?.phone || user?.phone || 'Not provided';
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      PENDING_CONSENT: { label: 'Pending Consent', class: 'pending' },
      ASSIGNED: { label: 'Assigned', class: 'pending' },
      IN_PROGRESS: { label: 'In Progress', class: 'pending' },
      COMPLETED: { label: 'Completed', class: 'verified' },
      ON_HOLD: { label: 'On Hold', class: 'not-requested' },
      UNABLE_TO_VERIFY: { label: 'Unable to Verify', class: 'rejected' },
    };
    const statusInfo =
      statusMap[status] || { label: status || 'Unknown', class: 'not-requested' };
    return statusInfo.label;
  };



  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStats = () => {
    const total = backgroundChecks.length;
    const pending = backgroundChecks.filter(c => c.status === 'PENDING_CONSENT' || c.status === 'ASSIGNED').length;
    const inProgress = backgroundChecks.filter(c => c.status === 'IN_PROGRESS').length;
    const completed = backgroundChecks.filter(c => c.status === 'COMPLETED').length;

    return { total, pending, inProgress, completed };
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const stats = getStats();

  // ==================== RENDER ====================
  return (
    <>
      <Header />

      {/* Role Indicator Banner */}
      <div style={{
        padding: '12px 20px',
        background: '#e3f2fd',
        borderBottom: '1px solid #ddd',
        textAlign: 'center',
        fontSize: '14px',
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px'
      }}>
        <span style={{ fontSize: '20px' }}>🏢</span>
        <span>Employer Dashboard</span>
        <span style={{ color: '#666', fontWeight: 'normal' }}>•</span>
        <span style={{ color: '#666', fontWeight: 'normal' }}>Request and manage background verifications</span>
      </div>

      <div className="dashboard-container" style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>

        {/* Welcome Section */}
        <div className="dashboard-header" style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Welcome back, {getDisplayName()}!</h1>
          <p style={{ color: '#666', fontSize: '1rem' }}>Manage verification requests and track candidate background checks</p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
            {success}
          </div>
        )}
        {error && (
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {/* Stats Overview */}
        <div className="dashboard-overview">
          <div className="stats-grid">
            {/* Total Requests */}
            <div className="stat-card total">
              <div className="stat-icon">📋</div>
              <div className="stat-content">
                <h3>{stats.total}</h3>
                <p>Total Requests</p>
                <small>All time</small>
              </div>
            </div>

            {/* Pending */}
            <div className="stat-card pending">
              <div className="stat-icon">⏳</div>
              <div className="stat-content">
                <h3>{stats.pending}</h3>
                <p>Pending</p>
                <small>Awaiting consent</small>
              </div>
            </div>

            {/* In Progress */}
            <div className="stat-card pending">
              <div className="stat-icon">🔄</div>
              <div className="stat-content">
                <h3>{stats.inProgress}</h3>
                <p>In Progress</p>
                <small>Being verified</small>
              </div>
            </div>

            {/* Completed */}
            <div className="stat-card verified">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <h3>{stats.completed}</h3>
                <p>Completed</p>
                <small>Verification done</small>
              </div>
            </div>
          </div>
        </div>

        {/* Main Dashboard Content */}
        <div className="dashboard-content">

          {/* Main Content Area */}
          <div className="dashboard-main">

            {/* ===== Job Listing Section ===== */}
            <div className="dashboard-section">
              <div className="dashboard-section-header">
                <h2>Job Listings</h2>
                <button className="btn-primary" onClick={() => navigate('/employer/jobs')}>
  Manage Job Postings
</button>

              </div>

            </div>

            {/* Background Checks Section */}
            <div className="dashboard-card">
              <div className="card-header">
                <h2>🔍 Recent Background Checks</h2>
                <button
                  className='btn-primary'
                  onClick={() => setShowRequestModal(true)}
                  style={{
                    padding: '0.5rem 1rem',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  + New Request
                </button>
              </div>

              {loadingChecks ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading background checks...</p>
                </div>
              ) : checksError ? (
                <div className="error-state">
                  <p>{checksError}</p>
                  <p>Unable to load background checks</p>
                </div>
              ) : backgroundChecks.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.875rem'
                  }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                        <th style={{ padding: '1rem', fontWeight: '600' }}>Candidate</th>
                        <th style={{ padding: '1rem', fontWeight: '600' }}>Scope</th>
                        <th style={{ padding: '1rem', fontWeight: '600' }}>Agency</th>
                        <th style={{ padding: '1rem', fontWeight: '600' }}>Status</th>
                        <th style={{ padding: '1rem', fontWeight: '600' }}>Requested</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backgroundChecks.map((check) => {
                        const candidateName =
                          check.candidate?.profile?.firstName || check.candidate?.profile?.lastName
                            ? `${check.candidate.profile.firstName || ''} ${check.candidate.profile.lastName || ''
                              }`.trim()
                            : 'N/A';

                        const candidateEmail = check.candidate?.email || 'N/A';

                        const agencyName =
                          check.agency?.profile?.firstName || check.agency?.profile?.lastName
                            ? `${check.agency.profile.firstName || ''} ${check.agency.profile.lastName || ''
                              }`.trim()
                            : 'Not assigned';

                        const agencyEmail = check.agency?.email || '';

                        return (
                          <tr key={check.id}>
                            {/* Candidate */}
                            <td>
                              <div style={{ fontWeight: 500 }}>{candidateName}</div>
                              <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                {candidateEmail}
                              </div>
                            </td>

                            {/* Scope */}
                            <td>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                                {check.scope?.map((item, idx) => (
                                  <span
                                    key={idx}
                                    style={{
                                      padding: '0.25rem 0.5rem',
                                      background: '#f3f4f6',
                                      borderRadius: '4px',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    {item}
                                  </span>
                                ))}
                              </div>
                            </td>

                            {/* Agency */}
                            <td>
                              <div style={{ fontWeight: 500 }}>{agencyName}</div>
                              <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                {agencyEmail || 'Not assigned'}
                              </div>
                            </td>

                            {/* Status & Date */}
                            <td>{getStatusBadge(check.status)}</td>
                            <td>{formatDate(check.createdAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    

                  </table>
                  {backgroundChecks.length > 5 && (
                    <div style={{ padding: '1rem', textAlign: 'center', borderTop: '1px solid #f3f4f6' }}>
                      <Link to="/employer/background-checks" style={{ color: '#3b82f6', textDecoration: 'none', fontSize: '0.875rem' }}>
                        View all {backgroundChecks.length} requests →
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No background check requests yet</p>
                  <button
                    onClick={() => setShowRequestModal(true)}
                    style={{
                      padding: '0.5rem 1rem',
                      background: 'linear-gradient(135deg, #0a66c2 0%, #004182 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      marginTop: '1rem'
                    }}
                  >
                    Request First Background Check
                  </button>
                </div>
              )}
            </div>

          </div>

          {/* Sidebar */}
          <div className="dashboard-sidebar">

            {/* Profile Info Card */}
            <div className="dashboard-card">
              <div className="card-header">
                <h3>👤 Profile Info</h3>
                <Link
                  to="/employer/profile"
                  style={{
                    fontSize: '0.875rem',
                    color: '#3b82f6',
                    textDecoration: 'none',
                    fontWeight: '500'
                  }}
                >
                  Edit Profile
                </Link>
              </div>
              <div className="profile-info">
                <div className="info-row">
                  <span className="info-label">Name:</span>
                  <span className="info-value">{getDisplayName()}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Email:</span>
                  <span className="info-value">{getDisplayEmail()}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Phone:</span>
                  <span className="info-value">{getDisplayPhone()}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Role:</span>
                  <span className="verification-badge validated">Employer</span>
                </div>
              </div>
            </div>

            {/* MFA Settings Card */}
            <div className="dashboard-card">
              <div className="card-header">
                <h3>🔒 Security Settings</h3>
              </div>
              <div className="mfa-settings">
                <div className="mfa-status">
                  <div className="mfa-icon">
                    {mfaSettings.mfaEnabled ? '🔒' : '🔓'}
                  </div>
                  <div className="mfa-info">
                    <h4>Two-Factor Authentication</h4>
                    <p style={{ fontSize: '0.875rem', color: '#666', margin: '0.25rem 0 0 0' }}>
                      {mfaSettings.mfaEnabled
                        ? `Enabled via ${mfaSettings.mfaType}`
                        : 'Add an extra layer of security to your account'}
                    </p>
                  </div>
                </div>
                <button
                  className="btn-primary"
                  onClick={() => setShowMFASettings(true)}
                  style={{
                    width: '100%',
                    marginTop: '1rem',
                    padding: '0.75rem',

                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  {mfaSettings.mfaEnabled ? 'Manage MFA' : 'Enable MFA'}
                </button>
              </div>
            </div>

            {/* Quick Actions Card */}
            <div className="dashboard-card">
              <div className="card-header">
                <h3>⚡ Quick Actions</h3>
              </div>
              <div className="action-buttons">
                <button
                  onClick={() => setShowRequestModal(true)}
                  className="action-btn"
                >
                  <span className="action-icon">🔍</span>
                  <span>Request Verification</span>
                </button>
                <Link to="/employer/candidates" className="action-btn">
                  <span className="action-icon">👥</span>
                  <span>View Candidates</span>
                </Link>
                <Link to="/change-password" className="action-btn">
                  <span className="action-icon">🔒</span>
                  <span>Change Password</span>
                </Link>
                <button onClick={handleLogout} className="action-btn logout-btn">
                  <span className="action-icon">🚪</span>
                  <span>Logout</span>
                </button>
                <Link to="/employer/references" className="action-btn">
  <span className="action-icon">📌</span>
  <span>Reference System</span>
</Link>

              </div>
            </div>

          </div>
        </div>

      </div>

      {/* MFA Settings Modal */}
      {showMFASettings && (
        <div className="dashboard-modal-overlay" onClick={() => setShowMFASettings(false)}>
          <div className="dashboard-modal-content mfa-simple-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dashboard-modal-header">
              <h2>Configure Two-Factor Authentication</h2>
              <button className="dashboard-close-btn" onClick={() => setShowMFASettings(false)}>×</button>
            </div>

            <div className="dashboard-modal-body">
              {/* MFA Type Selection */}
              <div className="mfa-type-options-simple">
                <label className={`mfa-type-card ${tempSettings.mfaType === 'email' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="mfaType"
                    value="email"
                    checked={tempSettings.mfaType === 'email'}
                    onChange={() => handleMfaTypeChange('email')}
                  />
                  <div className="card-content">
                    <div className="card-icon">📧</div>
                    <div className="card-text">
                      <h4>Email Verification</h4>
                      <p>Send verification codes to your email</p>
                    </div>
                  </div>
                </label>

                <label className={`mfa-type-card ${tempSettings.mfaType === 'sms' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="mfaType"
                    value="sms"
                    checked={tempSettings.mfaType === 'sms'}
                    onChange={() => handleMfaTypeChange('sms')}
                  />
                  <div className="card-content">
                    <div className="card-icon">📱</div>
                    <div className="card-text">
                      <h4>SMS Verification</h4>
                      <p>Send verification codes to your phone</p>
                    </div>
                  </div>
                </label>
              </div>

              {/* Enable/Disable MFA Button */}
              <button
                className="btn-enable-mfa"
                onClick={handleMFAToggle}
                disabled={loading}
              >
                <span className="btn-icon">🔒</span>
                {loading ? 'Processing...' : mfaSettings.mfaEnabled ? 'Update MFA' : 'Enable MFA'}
              </button>

              {/* Optional: Disable MFA Button */}
              {mfaSettings.mfaEnabled && (
                <button
                  className="btn-disable-mfa"
                  onClick={handleMFAToggle}
                  disabled={loading}
                >
                  Disable MFA
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/*Request Background Check Modal */}
      <RequestBackgroundCheckModal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        onSuccess={handleRequestSuccess}
      />
    </>
  );
};

export default EmployerDashboard;
